import { useCallback, useEffect, useMemo, useState } from "react";
import { ChefHat, Printer, Receipt, TriangleAlert, UserCog, Info } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { Button } from "@/components/ui/button";
import * as printerApi from "@/lib/printerApi";

const message = error =>
  error?.response?.data?.detail?.message ||
  (typeof error?.response?.data?.detail === "string" ? error.response.data.detail : null) ||
  error?.response?.data?.detail?.[0]?.msg ||
  error?.message ||
  "Could not reach the local server";

const call = async fn => {
  try {
    return { ok: true, data: await fn() };
  } catch (error) {
    return { ok: false, error: message(error) };
  }
};

const sameSet = (a, b) =>
  a.length === b.length && a.every(id => b.includes(id));

const NONE = Object.freeze([]);
const UNTOUCHED = Object.freeze({ items: null, tables: null, users: null });

const tableLabel = table =>
  table.label ? `${table.label} (${table.number})` : `Table ${table.number}`;

export default function PrintRoutingPanel() {
  const { token } = usePOS();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [edits, setEdits] = useState(UNTOUCHED);
  const [savingAxis, setSavingAxis] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const result = await call(() => printerApi.routingCatalog(token));
    if (result.ok) setCatalog(result.data);
    setError(result.ok ? null : result.error);
    setLoading(false);
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const printers = catalog?.printers ?? NONE;
  const categories = catalog?.categories ?? NONE;
  const tables = catalog?.tables ?? NONE;
  const users = catalog?.users ?? NONE;

  const selected = printers.find(p => p.id === selectedId) ?? printers[0] ?? null;

  const saved = useMemo(() => {
    if (!selected) return { items: [], tables: [], users: [] };
    return {
      items: categories.flatMap(category =>
        category.items.filter(item => item.printer_ids.includes(selected.id)).map(item => item.id)
      ),
      tables: tables.filter(t => t.bill_printer_id === selected.id).map(t => t.id),
      users: users.filter(u => u.bill_printer_id === selected.id).map(u => u.id),
    };
  }, [selected, categories, tables, users]);

  const ticked = axis => edits[axis] ?? saved[axis];
  const draft = {
    items: ticked("items"),
    tables: ticked("tables"),
    users: ticked("users"),
  };

  const defaultPrinter = printers.find(p => p.id === catalog?.default_printer_id) ?? null;

  const staffVisible = catalog?.may_view_staff_lock ?? catalog?.may_edit_staff_lock ?? false;
  const staffEditable = catalog?.may_edit_staff_lock ?? false;

  const setAxis = (axis, next) => setEdits(current => ({ ...current, [axis]: next }));

  const toggle = (axis, id) => {
    const now = ticked(axis);
    setAxis(axis, now.includes(id) ? now.filter(existing => existing !== id) : [...now, id]);
  };

  const toggleCategory = category => {
    const ids = category.items.map(item => item.id);
    const now = ticked("items");
    const all = ids.length > 0 && ids.every(id => now.includes(id));
    setAxis(
      "items",
      all ? now.filter(id => !ids.includes(id)) : [...now, ...ids.filter(id => !now.includes(id))]
    );
  };

  const selectPrinter = id => {
    setEdits(UNTOUCHED);
    setSelectedId(id);
  };

  const save = async (axis, setter, noun) => {
    setSavingAxis(axis);
    const result = await call(() => setter(selected.id, draft[axis], token));
    setSavingAxis(null);
    if (!result.ok) return toast.error(`${noun} not saved — ${result.error}`);
    toast.success(`${noun} updated for ${selected.name}`);
    setAxis(axis, null);
    await load();
  };

  const dirty = axis => edits[axis] !== null && !sameSet(edits[axis], saved[axis]);

  const halfLockCount = useMemo(
    () => users.filter(u => u.bill_printer_locked && !u.bill_printer_id).length,
    [users]
  );

  const unrouted = useMemo(
    () =>
      categories.flatMap(category =>
        category.items.filter(item => item.printer_ids.length === 0)
      ).length,
    [categories]
  );

  if (loading && !catalog) {
    return <section className="bg-card border rounded-2xl p-6 shadow-sm flex flex-col h-full items-center justify-center min-h-[400px]" data-testid="print-routing-panel">
      <p className="text-meta text-muted-foreground animate-pulse font-medium" data-testid="print-routing-loading">Reading routing…</p>
    </section>;
  }

  return <section className="bg-card border rounded-2xl shadow-sm flex flex-col h-full" data-testid="print-routing-panel">
    <header className="p-6 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <span className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Printer className="w-5 h-5"/>
        </span>
        <div>
          <h3 className="text-title font-semibold text-foreground">Print routing</h3>
          <p className="text-meta text-muted-foreground font-medium">Where each item's KOT and each bill actually comes out.</p>
        </div>
      </div>
    </header>

    <div className="p-6 space-y-6">
      {error && (
        <div className="bg-destructive/10 text-destructive border border-destructive/20 p-4 rounded-xl flex items-start gap-3" role="alert" data-testid="print-routing-error">
          <TriangleAlert className="w-5 h-5 shrink-0 mt-0.5"/>
          <span className="text-body font-semibold">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-info/50 text-info p-4 rounded-xl border border-info/40 flex items-start gap-3" data-testid="print-routing-precedence">
          <Info className="w-5 h-5 shrink-0 mt-0.5 text-info"/>
          <p className="text-meta font-medium leading-relaxed">
            A KOT line prints on every printer ticked for that item, or on the default printer if
            none is ticked; a bill prints on the locked printer of whoever settles it if they have
            one, else the table's, else the default.
          </p>
        </div>
        
        {!error && (
          <div className="bg-muted/50 text-muted-foreground p-4 rounded-xl border flex items-start gap-3" data-testid="print-routing-default">
            <Info className="w-5 h-5 shrink-0 mt-0.5 opacity-50"/>
            <p className="text-meta font-medium leading-relaxed">
              {defaultPrinter
                ? <>Default printer: <strong>{defaultPrinter.name}</strong>. {unrouted} menu item(s) route nowhere and fall back to it.</>
                : `No active printer resolves as the default — ${unrouted} unrouted menu item(s) will not print at all.`}
            </p>
          </div>
        )}
      </div>

      {!printers.length && !error && (
        <div className="py-12 flex items-center justify-center border-2 border-dashed rounded-xl">
          <p className="text-meta text-muted-foreground font-medium" data-testid="print-routing-empty">
            No printers configured. Add one in the printer registry before routing anything to it.
          </p>
        </div>
      )}

      {selected && <>
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide border-b" data-testid="routing-printer-tabs">
          {printers.map((printer, index) => (
            <button
              key={printer.id}
              className={`flex flex-col px-4 py-2 font-semibold text-body rounded-t-lg transition-colors border-b-2 whitespace-nowrap min-w-[120px] ${printer.id === selected.id ? 'border-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:bg-muted hover:text-foreground'}`}
              onClick={() => selectPrinter(printer.id)}
              data-testid={`routing-printer-${index}-tab`}
              aria-pressed={printer.id === selected.id}
            >
              <span className={`text-body ${printer.id === selected.id ? 'text-primary font-semibold' : ''}`}>{printer.name}</span>
              {!printer.is_active && <small className="text-meta font-medium text-danger mt-0.5" data-testid={`routing-printer-${index}-disabled`}>disabled</small>}
            </button>
          ))}
        </div>

        {!selected.is_active && (
          <div className="bg-warning-subtle text-warning p-4 rounded-xl border border-warning/40 text-meta font-medium" data-testid="routing-printer-disabled-warning">
            <strong>{selected.name} is disabled.</strong> Routing saved here takes effect only once it is enabled —
            until then these items and tables fall back to the default printer.
          </div>
        )}

        <div className="space-y-6">
          {/* Items */}
          <div className="border rounded-xl overflow-hidden" data-testid="routing-items">
            <header className="bg-muted/30 p-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <ChefHat className="w-5 h-5 text-muted-foreground"/>
                <div className="flex flex-col">
                  <small className="text-meta font-medium text-primary">KOT · MENU ITEMS</small>
                  <h4 className="text-lede font-semibold text-foreground">What prints in this station</h4>
                </div>
              </div>
              <Button variant="action"
                size="sm"
                className="font-bold shadow-sm h-9"
                onClick={() => save("items", printerApi.setPrinterMenuItems, "Item routing")}
                disabled={savingAxis === "items" || !dirty("items")}
                data-testid="save-routing-items-button"
              >{savingAxis === "items" ? "Saving…" : "Save item routing"}</Button>
            </header>

            <div className="p-4 bg-background">
              {!categories.length && <p className="py-8 text-center text-meta text-muted-foreground font-medium" data-testid="routing-items-empty">No menu items yet.</p>}
              
              <div className="space-y-6">
                {categories.map((category, catIndex) => {
                  const ids = category.items.map(item => item.id);
                  const all = ids.length > 0 && ids.every(id => draft.items.includes(id));
                  return <div key={category.id} data-testid={`routing-category-${catIndex}`}>
                    <label className="flex items-center gap-3 py-2 cursor-pointer hover:bg-muted/50 rounded-lg px-2 -mx-2 transition-colors">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-input text-primary focus:ring-primary"
                        checked={all}
                        onChange={() => toggleCategory(category)}
                        disabled={!ids.length}
                        data-testid={`routing-category-${catIndex}-checkbox`}
                        aria-label={`Route every item in ${category.name} to ${selected.name}`}
                      />
                      <div className="flex flex-col">
                        <strong className="text-body font-semibold text-foreground">{category.name}</strong>
                        <small className="text-meta text-muted-foreground font-medium">ticks every item below</small>
                      </div>
                    </label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 mt-2 ml-7">
                      {category.items.map((item, itemIndex) => {
                        const elsewhere = item.printer_ids.filter(id => id !== selected.id);
                        return <label className="flex items-start gap-2.5 p-2 rounded-lg border hover:bg-muted/30 cursor-pointer transition-colors" key={item.id} data-testid={`routing-item-${category.id}-${itemIndex}`}>
                          <input
                            type="checkbox"
                            className="w-4 h-4 rounded border-input text-primary focus:ring-primary mt-0.5"
                            checked={draft.items.includes(item.id)}
                            onChange={() => toggle("items", item.id)}
                            data-testid={`routing-item-${item.id}-checkbox`}
                            aria-label={`Print ${item.name} on ${selected.name}`}
                          />
                          <div className="flex flex-col">
                            <span className="text-body font-semibold text-foreground leading-tight">{item.name}</span>
                            {elsewhere.length > 0 && (
                              <small className="text-meta font-medium text-muted-foreground mt-1" data-testid={`routing-item-${item.id}-elsewhere`}>+{elsewhere.length} other</small>
                            )}
                          </div>
                        </label>;
                      })}
                    </div>
                  </div>;
                })}
              </div>
            </div>
          </div>

          {/* Tables */}
          <div className="border rounded-xl overflow-hidden" data-testid="routing-tables">
            <header className="bg-muted/30 p-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Receipt className="w-5 h-5 text-muted-foreground"/>
                <div className="flex flex-col">
                  <small className="text-meta font-medium text-primary">BILL · TABLES</small>
                  <h4 className="text-lede font-semibold text-foreground">Whose bills come out here</h4>
                </div>
              </div>
              <Button variant="action"
                size="sm"
                className="font-bold shadow-sm h-9"
                onClick={() => save("tables", printerApi.setPrinterTables, "Table routing")}
                disabled={savingAxis === "tables" || !dirty("tables")}
                data-testid="save-routing-tables-button"
              >{savingAxis === "tables" ? "Saving…" : "Save table routing"}</Button>
            </header>

            <div className="p-4 bg-background">
              {!tables.length && <p className="py-8 text-center text-meta text-muted-foreground font-medium" data-testid="routing-tables-empty">No tables yet.</p>}

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {tables.map(table => {
                  const elsewhere = table.bill_printer_id && table.bill_printer_id !== selected.id;
                  return <label className="flex items-start gap-2.5 p-3 rounded-xl border hover:bg-muted/30 cursor-pointer transition-colors" key={table.id} data-testid={`routing-table-${table.id}`}>
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-input text-primary focus:ring-primary mt-0.5"
                      checked={draft.tables.includes(table.id)}
                      onChange={() => toggle("tables", table.id)}
                      data-testid={`routing-table-${table.id}-checkbox`}
                      aria-label={`Print ${tableLabel(table)} bills on ${selected.name}`}
                    />
                    <div className="flex flex-col">
                      <span className="text-body font-semibold text-foreground leading-tight">{tableLabel(table)}</span>
                      {elsewhere && (
                        <small className="text-meta font-medium text-warning mt-1" data-testid={`routing-table-${table.id}-elsewhere`}>
                          now on {printers.find(p => p.id === table.bill_printer_id)?.name ?? `printer #${table.bill_printer_id}`}
                        </small>
                      )}
                    </div>
                  </label>;
                })}
              </div>
            </div>
          </div>

          {/* Staff Lock */}
          {!staffVisible && (
            <div className="bg-muted/50 p-3 rounded-lg text-meta text-muted-foreground font-medium" data-testid="routing-users-forbidden">
              Staff bill locks are managed by an administrator.
            </div>
          )}

          {staffVisible && <div className="border rounded-xl overflow-hidden" data-testid="routing-users">
            <header className="bg-muted/30 p-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <UserCog className="w-5 h-5 text-muted-foreground"/>
                <div className="flex flex-col">
                  <small className="text-meta font-medium text-primary">BILL · STAFF LOCK</small>
                  <h4 className="text-lede font-semibold text-foreground">Staff whose bills are forced here</h4>
                </div>
              </div>
              {staffEditable && <Button variant="action"
                size="sm"
                className="font-bold shadow-sm h-9"
                onClick={() => save("users", printerApi.setPrinterUsers, "Staff bill lock")}
                disabled={savingAxis === "users" || !dirty("users")}
                data-testid="save-routing-users-button"
              >{savingAxis === "users" ? "Saving…" : "Save staff lock"}</Button>}
            </header>

            <div className="p-4 bg-background space-y-4">
              <div className="bg-muted/50 p-3 rounded-lg text-meta text-muted-foreground font-medium" data-testid="routing-users-hint">
                Whoever settles a bill prints it here if they are ticked, whatever the billing screen
                picks — it follows the person, not the orders they opened. Leave everyone unticked
                unless a till really is tied to one printer.
              </div>

              {!staffEditable && (
                <div className="bg-danger-subtle text-danger p-3 rounded-lg text-meta font-medium border border-danger/40" data-testid="routing-users-readonly">
                  Read-only for your role — changing a staff lock needs permission to edit users.
                </div>
              )}

              {staffEditable && halfLockCount > 0 && (
                <div className="bg-warning-subtle text-warning p-3 rounded-lg text-meta font-medium border border-warning/40" data-testid="routing-users-half-lock-repair">
                  {halfLockCount} staff carry a lock that names no printer (a leftover from an old
                  update). It routes nothing today; saving this section clears it.
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {users.map(user => {
                  const elsewhere = user.bill_printer_id && user.bill_printer_id !== selected.id;
                  const halfLocked = user.bill_printer_locked && !user.bill_printer_id;
                  return <label className="flex items-start gap-2.5 p-3 rounded-xl border hover:bg-muted/30 cursor-pointer transition-colors" key={user.id} data-testid={`routing-user-${user.id}`}>
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-input text-primary focus:ring-primary mt-0.5"
                      checked={draft.users.includes(user.id)}
                      onChange={() => toggle("users", user.id)}
                      disabled={!staffEditable}
                      data-testid={`routing-user-${user.id}-checkbox`}
                      aria-label={`Lock ${user.name}'s bills to ${selected.name}`}
                    />
                    <div className="flex flex-col">
                      <span className="text-body font-semibold text-foreground leading-tight">{user.name}</span>
                      <small className="text-meta text-muted-foreground font-medium mt-0.5">{user.role}</small>
                      {elsewhere && (
                        <small className="text-meta font-medium text-warning mt-1" data-testid={`routing-user-${user.id}-elsewhere`}>
                          now on {printers.find(p => p.id === user.bill_printer_id)?.name ?? `printer #${user.bill_printer_id}`}
                        </small>
                      )}
                      {halfLocked && (
                        <small className="text-meta font-medium text-danger mt-1" data-testid={`routing-user-${user.id}-half-lock`}>
                          locked to no printer — ignored
                        </small>
                      )}
                    </div>
                  </label>;
                })}
              </div>
            </div>
          </div>}

          {/* Effective routing summary */}
          <div className="border rounded-xl overflow-hidden mt-8" data-testid="routing-summary">
            <header className="bg-muted/30 p-4 border-b">
              <small className="text-meta font-medium text-primary">Effective routing</small>
              <h4 className="text-lede font-semibold text-foreground mt-0.5">Every printer, as the server will read it</h4>
            </header>
            
            <div className="divide-y bg-background">
              {printers.map((printer, index) => {
                const itemCount = categories.flatMap(c => c.items).filter(i => i.printer_ids.includes(printer.id)).length;
                const tableCount = tables.filter(t => t.bill_printer_id === printer.id).length;
                const userCount = users.filter(u => u.bill_printer_id === printer.id).length;
                return <div className="flex items-center p-4 gap-4" key={printer.id} data-testid={`routing-summary-${index}`}>
                  <strong className="w-1/4 min-w-[120px] text-body font-semibold text-foreground">{printer.name}</strong>
                  <div className="flex-1 flex gap-4 text-meta font-medium text-muted-foreground tabular-nums">
                    <span className="w-1/4">{itemCount} item(s)</span>
                    <span className="w-1/4">{tableCount} table(s)</span>
                    {staffVisible && <span className="w-1/4">{userCount} staff lock(s)</span>}
                  </div>
                  {printer.id === catalog?.default_printer_id && <span className="text-meta font-medium px-2 py-0.5 bg-primary/10 text-primary rounded-md" data-testid={`routing-summary-${index}-default`}>default</span>}
                </div>;
              })}
            </div>
          </div>
        </div>
      </>}
    </div>
  </section>;
}
